/**
 * Searchable Triple Master picker for Bet Ticker filters.
 * Popover + SearchInput (same pattern as reports MemberPicker), single-select.
 */

import { Icon, Popover, SearchInput, Spinner } from '@w7/ui'
import { useMemo, useRef, useState } from 'react'

export type TmOption = { id: string; username: string }

/** Filter TM list by username substring (case-insensitive). */
export function filterTmOptions(options: TmOption[], needle: string): TmOption[] {
  const text = needle.trim().toLowerCase()
  if (!text) return options
  return options.filter((tm) => tm.username.toLowerCase().includes(text))
}

export function TmPicker({
  value,
  options,
  onChange,
  loading = false,
}: {
  value: string
  options: TmOption[]
  onChange: (tmId: string) => void
  loading?: boolean
}) {
  const anchor = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [needle, setNeedle] = useState('')

  const selected = options.find((tm) => tm.id === value)
  const caption = selected?.username ?? 'All TMs'
  const shown = useMemo(() => filterTmOptions(options, needle), [options, needle])

  const pick = (id: string) => {
    onChange(id)
    setOpen(false)
    setNeedle('')
  }

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="w7-bm-select w7-bm-select--tm w7-bm-tm-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Select TM: ${caption}`}
        disabled={loading}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="w7-bm-tm-trigger-label">{loading ? 'Loading TMs…' : caption}</span>
        <Icon name="chevron-down" size={14} />
      </button>

      <Popover
        open={open}
        onClose={() => {
          setOpen(false)
          setNeedle('')
        }}
        anchorRef={anchor}
        label="Select TM"
        width={280}
      >
        <div className="w7-bm-tm-popover">
          <SearchInput
            label="Search TMs"
            placeholder="Search TM"
            value={needle}
            onChange={setNeedle}
          />
          {loading ? (
            <span className="w7-bm-tm-status">
              <Spinner size={14} /> Loading Triple Masters
            </span>
          ) : (
            <div className="w7-bm-tm-list" role="listbox" aria-label="Triple Masters">
              <button
                type="button"
                role="option"
                aria-selected={!value}
                className="w7-bm-tm-option"
                data-selected={!value || undefined}
                onClick={() => pick('')}
              >
                All TMs
              </button>
              {shown.map((tm) => (
                <button
                  key={tm.id}
                  type="button"
                  role="option"
                  aria-selected={value === tm.id}
                  className="w7-bm-tm-option"
                  data-selected={value === tm.id || undefined}
                  onClick={() => pick(tm.id)}
                >
                  {tm.username}
                </button>
              ))}
              {shown.length === 0 ? (
                <p className="w7-bm-tm-status">
                  {needle.trim() ? `No TMs match “${needle.trim()}”.` : 'No Triple Masters found.'}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </Popover>
    </>
  )
}
